/**
 * GodRaysPass — screen-space radial blur god rays (GPU Gems 3, Ch. 13).
 *
 * Projects the sun to screen space, then for each pixel marches backward
 * along the direction toward the sun, sampling scene depth to distinguish
 * sky (near depth = 1.0) from geometry. Accumulates weighted samples with
 * exponential decay to produce light shaft rays.
 *
 * Usage:
 *   const scenePass = pass(scene, camera);
 *   const color     = scenePass.getTextureNode('output');
 *   const depth     = scenePass.getTextureNode('depth');
 *   const { node, uniforms } = createGodRaysNode(color, depth);
 *   // update uniforms each frame:
 *   const sunResult = projectSunToScreen(dayNight.getSunDirection(), camera);
 *   uniforms.sunScreenPos.value.set(sunResult.x, sunResult.y);
 *   uniforms.intensity.value = sunResult.sunBelowHorizon ? 0 : 1;
 *   // composite:
 *   outputNode = outputNode.add(node);
 */

import * as THREE from 'three/webgpu';
import { Fn, If, Loop, uniform, float, vec4, uv, clamp } from 'three/tsl';
import { skyMask } from '../depth/skyMask.js';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Configuration for the radial blur god rays effect. */
export interface GodRaysConfig {
  /** Number of radial blur samples per pixel (default: 64). */
  numSamples: number;
  /** Per-step exponential decay factor (default: 0.97). Higher = longer rays. */
  decay: number;
  /** Final brightness multiplier (default: 0.3). */
  exposure: number;
  /** Step length scale along the ray direction (default: 1.0). */
  density: number;
  /** Per-sample weight multiplier (default: 0.01). */
  weight: number;
}

/** Result of projecting the sun direction to screen UV space. */
export interface SunScreenResult {
  /** Horizontal screen UV coordinate in [0, 1]. */
  x: number;
  /** Vertical screen UV coordinate in [0, 1]. */
  y: number;
  /** True when the sun is below the horizon (sunDir.y < 0). */
  sunBelowHorizon: boolean;
}

/**
 * The set of live uniforms returned by createGodRaysNode.
 * Update these every frame before rendering.
 */
export interface GodRaysUniforms {
  /** Sun position in screen UV [0,1]. Set via projectSunToScreen(). */
  sunScreenPos: { value: THREE.Vector2 };
  /** Overall intensity multiplier. Set to 0 when sunBelowHorizon. */
  intensity: { value: number };
  /** Per-step decay (mirrors GodRaysConfig.decay). */
  decay: { value: number };
  /** Final brightness multiplier (mirrors GodRaysConfig.exposure). */
  exposure: { value: number };
  /** Step length scale (mirrors GodRaysConfig.density). */
  density: { value: number };
  /** Per-sample weight (mirrors GodRaysConfig.weight). */
  weight: { value: number };
}

/** Return type of createGodRaysNode. */
export interface GodRaysNodeResult {
  /**
   * TSL output node (ShaderCallNodeInternal / Node).
   * Add to the scene-color node for compositing: `outputNode = outputNode.add(node)`.
   */
  node: unknown;
  /** Live uniforms — update sunScreenPos + intensity every frame. */
  uniforms: GodRaysUniforms;
  /** Merged config (defaults + user overrides). */
  config: GodRaysConfig;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG: GodRaysConfig = {
  numSamples: 64,
  decay: 0.97,
  exposure: 0.3,
  density: 1.0,
  weight: 0.01,
};

// ─── Reusable scratch vectors (zero-alloc per-frame) ─────────────────────────

const _sunPos = new THREE.Vector3();

// ─── projectSunToScreen ──────────────────────────────────────────────────────

/**
 * Projects a world-space sun direction vector to screen UV coordinates.
 *
 * The sun is placed far along `sunDir` from the camera origin, projected
 * into clip space, then remapped to [0, 1] UV and clamped. If the
 * resulting clip-space W is near zero the result is clamped rather than
 * producing NaN.
 *
 * @param sunDir  Normalized world-space direction toward the sun.
 * @param camera  Active camera (must have up-to-date matrices).
 * @returns       Screen UV position and horizon flag.
 */
export function projectSunToScreen(sunDir: THREE.Vector3, camera: THREE.Camera): SunScreenResult {
  const sunBelowHorizon = sunDir.y < 0;

  // Place the sun as a world-space point far along sunDir from the camera,
  // then use Three.js .project() which handles the full MVP + perspective divide.
  const SUN_DISTANCE = 1e5;
  // WHY: `camera.position` is LOCAL and `.project()` reads matrixWorldInverse
  // — for a camera under a moving/rotating rig (RenderSub runs before the
  // renderer resolves the graph) the sun was projected through last frame's
  // rig pose from the rig-local origin: the shafts lagged the turn.
  camera.updateWorldMatrix(true, false);
  _sunPos.setFromMatrixPosition(camera.matrixWorld).addScaledVector(sunDir, SUN_DISTANCE);

  // .project() transforms to NDC [-1, 1] via camera VP matrix.
  // Can produce NaN/Infinity when the sun is exactly on the frustum boundary.
  const ndc = _sunPos.project(camera);

  // Guard against NaN/Infinity (e.g. sun exactly perpendicular to view axis)
  const rawX = Number.isFinite(ndc.x) ? ndc.x : Math.sign(sunDir.x) || 0;
  const rawY = Number.isFinite(ndc.y) ? ndc.y : Math.sign(sunDir.y) || 0;

  // Clamp NDC to [-1, 1] to handle behind-camera cases (w < 0 flips coords)
  const ndcX = Math.max(-1, Math.min(1, rawX));
  const ndcY = Math.max(-1, Math.min(1, rawY));

  // Remap NDC [-1, 1] → UV [0, 1].
  // Note: Three.js TSL uv() abstracts the WebGL/WebGPU Y-flip internally,
  // so this standard mapping is correct for both backends.
  const x = ndcX * 0.5 + 0.5;
  const y = ndcY * 0.5 + 0.5;

  return { x, y, sunBelowHorizon };
}

// ─── shouldRunGodRaysMarch ────────────────────────────────────────────────────

/**
 * Pure mirror of the TSL-side `If(uIntensity.greaterThan(0), …)` guard that
 * wraps the 64-sample radial-blur march inside `createGodRaysNode` below
 * (Finding 7, 2026-07-25 perf audit). `GodRaysRuntimeSystem`
 * (GodRaysModule.ts) forces `intensity` to exactly 0 when the sun is below
 * the horizon or behind the camera; in that case the march used to run
 * unconditionally and compute a result that got multiplied by zero at the
 * very end anyway — 64 depth-texture fetches per pixel for a result that was
 * always going to be black. Skipping the march is a pure win with no visual
 * difference: `accumulated` never leaves its initial 0, byte-identical to
 * the old "march then ×0" output, and there is no history/accumulation
 * buffer here for a skipped frame to leave stale (the shader recomputes
 * `vec4(brightness,…)` from scratch every frame; there is nothing to reuse).
 *
 * Deliberately `> 0`, not a small-epsilon or "≈0" test: any nonzero
 * intensity — including a fractional fade value from `alignmentThreshold`
 * gating — must still run the march, or a horizon-crossing fade-in/out would
 * visibly clip early. There is no GPU in this repo's test environment to
 * exercise the actual TSL branch (see docs/perf/2026-07-25-engine-graphics-
 * findings.md, measurement caveat 1), so this pure function pins the exact
 * intended semantics and is unit-tested directly instead — see
 * tests/unit/godrays-pass.test.ts.
 */
export function shouldRunGodRaysMarch(intensity: number): boolean {
  return intensity > 0;
}

// ─── createGodRaysNode ───────────────────────────────────────────────────────

/**
 * Creates a screen-space radial blur god rays TSL node.
 *
 * Algorithm (GPU Gems 3, Ch. 13 "Volumetric Light Scattering as a Post-Process"):
 *   for each pixel at UV `P`:
 *     dir = normalize(sunUV - P) * stepLength
 *     sampleUV = P
 *     accumulated = 0
 *     decayFactor = 1
 *     for i in 0..numSamples:
 *       sampleUV += dir
 *       depthSample = texture(sceneDepth, sampleUV)
 *       // sky = the depth clear value (nothing drawn); see render/depth/skyMask.ts
 *       skyness = skyMask(depthSample)
 *       accumulated += skyness * weight * decayFactor
 *       decayFactor *= decay
 *     output = vec4(accumulated * exposure * intensity)
 *
 * @param _sceneColor TSL node for scene color texture (reserved for future colored-ray variant).
 * @param sceneDepth  TSL node for scene depth texture (sampled per step).
 * @param config      Optional config overrides.
 */
export function createGodRaysNode(
  _sceneColor: unknown,
  sceneDepth: unknown,
  config?: Partial<GodRaysConfig>,
): GodRaysNodeResult {
  const merged: GodRaysConfig = { ...DEFAULT_CONFIG, ...config };

  // ── Live uniforms ────────────────────────────────────────────────────────
  const uSunScreenPos = uniform(new THREE.Vector2(0.5, 0.8));
  const uIntensity    = uniform(1.0);
  const uDecay        = uniform(merged.decay);
  const uExposure     = uniform(merged.exposure);
  const uDensity      = uniform(merged.density);
  const uWeight       = uniform(merged.weight);

  // ── TSL radial blur kernel ───────────────────────────────────────────────
  const godRaysFn = Fn(() => {
    // Current pixel UV
    const pixelUV = uv().toVar();

    // Direction from current pixel toward sun, scaled by step length.
    // uDensity is a live uniform so density can be tuned at runtime.
    const toSun = (uSunScreenPos as any).sub(pixelUV);
    const stepLen = (uDensity as any).div(float(merged.numSamples));
    const dir = (toSun as any).mul(stepLen).toVar();

    // Accumulator and decay factor
    const accumulated = float(0).toVar();
    const decayFactor  = float(1.0).toVar();
    // Independent mutable copy — must not alias pixelUV
    const sampleUV     = pixelUV.toVar();

    // Finding 7 (2026-07-25 perf audit): skip the whole 64-sample march when
    // intensity is exactly 0 (sun below horizon / behind camera — see
    // GodRaysRuntimeSystem in GodRaysModule.ts). This is a uniform branch
    // (uIntensity is the same value for every pixel in the draw), so GPUs
    // predicate/skip the whole loop body with no wave divergence.
    // `accumulated`/`decayFactor` keep their initial 0/1 values when skipped,
    // so `brightness` below still comes out exactly 0 — byte-identical to
    // the old "march unconditionally, then ×0" result. Mirrors
    // shouldRunGodRaysMarch() above, unit-tested directly since there is no
    // GPU here to exercise the branch itself.
    If((uIntensity as any).greaterThan(0), () => {
      // Walk numSamples steps toward the sun
      Loop(merged.numSamples, () => {
        // Advance sample position
        (sampleUV as any).addAssign(dir);

        // Clamp UV to [0, 1] to prevent screen-edge halo artifacts.
        // Without clamping, out-of-bounds samples read the clamped-to-edge
        // texel repeatedly, producing bright fringes at screen borders.
        const clampedUV = clamp(sampleUV as any, float(0), float(1));

        // Sample the depth buffer at the clamped UV
        // depth near 1.0 → sky pixel (bright contribution)
        // depth near 0.0 → geometry (occluded)
        const depthSample = (sceneDepth as any).sample
          ? (sceneDepth as any).sample(clampedUV)
          : (sceneDepth as any).uv
            ? (sceneDepth as any).uv(clampedUV)
            : (sceneDepth as any);

        // Sky mask: 1 where nothing was drawn, 0 on geometry. The helper also
        // does the `.r` reduction (a vec4 depth sample into step() then into a
        // float accumulator is a TSL type mismatch). WHY the helper: the old
        // `step(0.999, depth)` is z ≈ near/0.001 = 50 m on Atlas's camera, so
        // NO geometry past 50 m occluded a shaft — rays shone straight through
        // distant mountains. See render/depth/skyMask.ts.
        const sky = skyMask(depthSample);

        // Weighted contribution with exponential decay
        const contribution = (sky as any).mul(uWeight).mul(decayFactor);
        (accumulated as any).addAssign(contribution);
        (decayFactor as any).mulAssign(uDecay);
      });
    });

    // Scale by exposure and intensity
    const brightness = (accumulated as any).mul(uExposure).mul(uIntensity);
    return vec4(brightness, brightness, brightness, 0.0); // additive: contribute no alpha
  });

  const node = godRaysFn();

  // ── Return ───────────────────────────────────────────────────────────────
  const uniforms: GodRaysUniforms = {
    sunScreenPos: uSunScreenPos as any,
    intensity:    uIntensity as any,
    decay:        uDecay as any,
    exposure:     uExposure as any,
    density:      uDensity as any,
    weight:       uWeight as any,
  };

  return { node, uniforms, config: merged };
}
